import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { Button, Input, Alert, Spin } from "antd";
import DOMPurify from "dompurify";
import DocumentosAsociados from "./DocumentoAsociado";

function ComentariosGlpi() {
  const [rows, setRows] = useState([]);
  const [adjuntos, setAdjuntos] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [numeroCaso, setNumeroCaso] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comentarioCreacion, setComentarioCreacion] = useState("");

  const registrosPorPagina = 10;

  const fetchData = useCallback(async () => {
    if (!numeroCaso) {
      setError("Por favor ingrese un número de caso.");
      setRows([]);
      setAdjuntos([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [comentariosRes, comentariosCasoRes, adjuntosRes] = await Promise.all([
        axios.get(`https://contratosback.azurewebsites.net/api/v1/ObtieneAcciones/${numeroCaso}`),
        axios.get(
          `https://contratosback.azurewebsites.net/api/v1/ObtenerComentariosCasos/${numeroCaso}`
        ),
        axios.get(`https://apioraclecpqicontec.azurewebsites.net/api/ObtenerAdjunto/${numeroCaso}`),
      ]);

      if (comentariosCasoRes.data.length > 0) {
        setComentarioCreacion(comentariosCasoRes.data[0].creacion || "");
      }

      const formatoFecha = (fecha) =>
        new Date(fecha).toLocaleString("es-CO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

      const comentariosData = comentariosRes.data.length
        ? comentariosRes.data.map((item) => ({
            id: item.id,
            items_id: item.items_id,
            date_mod: formatoFecha(item.date_mod),
            user_name: item.user_name,
            new_value: (
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.new_value) }} />
            ),
          }))
        : [];

      const comentariosCasoData = comentariosCasoRes.data.length
        ? comentariosCasoRes.data.map((item) => ({
            id: item.id,
            items_id: item.tickets_id,
            date_mod: formatoFecha(item.date_mod),
            user_name: `Usuario ${item.users_id}`,
            new_value: <div style={{ whiteSpace: "pre-wrap" }}>{item.content}</div>,
          }))
        : [];

      const adjuntosData = adjuntosRes.data.length
        ? adjuntosRes.data.map((item) => ({
            id: item.id,
            nombre: item.name,
            fechaCreacion: item.date_creation,
            usuarioCreacion: item.users_id,
            archivo: (
              <a href={item.descarga} target="_blank" rel="noopener noreferrer">
                {item.filename}
              </a>
            ),
          }))
        : [];

      const todasLasFilas = [...comentariosCasoData, ...comentariosData];

      if (!todasLasFilas.length && !adjuntosData.length) {
        setError("No hay información disponible para este caso.");
      }

      setRows(todasLasFilas);
      setAdjuntos(adjuntosData);
      setPagina(1);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Error al obtener los datos. Intente de nuevo.");
      setRows([]);
      setAdjuntos([]);
    } finally {
      setLoading(false);
    }
  }, [numeroCaso]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) =>
      Object.entries(row).some(([key, value]) => {
        if (typeof value === "string") {
          return value.toLowerCase().includes(searchTerm);
        }
        if (key === "new_value" && React.isValidElement(value)) {
          const div = document.createElement("div");
          div.innerHTML = value.props.dangerouslySetInnerHTML
            ? value.props.dangerouslySetInnerHTML.__html
            : value.props.children;
          return div.textContent.toLowerCase().includes(searchTerm);
        }
        return false;
      })
    );
  }, [rows, searchTerm]);

  const totalPaginas = Math.ceil(filteredRows.length / registrosPorPagina);
  const rowsPaginados = useMemo(() => {
    const startIndex = (pagina - 1) * registrosPorPagina;
    return filteredRows.slice(startIndex, startIndex + registrosPorPagina);
  }, [filteredRows, pagina, registrosPorPagina]);

  const columnsComentarios = [
    { Header: "ID", accessor: "id" },
    { Header: "Ticket ID", accessor: "items_id" },
    { Header: "Usuario", accessor: "user_name" },
    { Header: "Fecha", accessor: "date_mod" },
    { Header: "Acción", accessor: "new_value" },
  ];

  const columnsAdjuntos = [
    { Header: "ID", accessor: "id" },
    { Header: "Nombre", accessor: "nombre" },
    { Header: "Fecha Creación", accessor: "fechaCreacion" },
    { Header: "Usuario Creación", accessor: "usuarioCreacion" },
    { Header: "Archivo", accessor: "archivo" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Historial de Comentarios y Adjuntos
                </MDTypography>
              </MDBox>
              <MDBox pt={3} px={2}>
                <Input
                  placeholder="Número del caso..."
                  value={numeroCaso}
                  onChange={(e) => setNumeroCaso(e.target.value)}
                  style={{ margin: "10px 10px 10px 0", width: "250px" }}
                  disabled={loading}
                />
                <Button type="primary" onClick={fetchData} disabled={loading}>
                  {loading ? "Buscando..." : "Buscar"}
                </Button>
                {error && (
                  <Alert message={error} type="warning" showIcon style={{ margin: "10px 0" }} />
                )}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" mt={3}>
                    <Spin size="large" />
                  </MDBox>
                ) : (
                  <>
                    {rows.length > 0 && (
                      <>
                        <MDTypography variant="h6" mt={3}>
                          Comentarios
                        </MDTypography>
                        {comentarioCreacion && (
                          <MDBox mt={1}>
                            <MDTypography variant="body2" fontWeight="bold">
                              Comentario de creación:
                            </MDTypography>
                            <MDTypography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                              {comentarioCreacion}
                            </MDTypography>
                          </MDBox>
                        )}

                        <Input
                          placeholder="Buscar en comentarios..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                          style={{ margin: "10px 20px", width: "650px" }}
                        />
                        <DataTable
                          table={{ columns: columnsComentarios, rows: rowsPaginados }}
                          isSorted={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                        />
                        <MDBox display="flex" justifyContent="center" alignItems="center" mt={2}>
                          <Button
                            onClick={() => setPagina((p) => Math.max(p - 1, 1))}
                            disabled={pagina === 1}
                          >
                            Anterior
                          </Button>
                          <MDTypography mx={2}>
                            {pagina} de {totalPaginas}
                          </MDTypography>
                          <Button
                            onClick={() => setPagina((p) => Math.min(p + 1, totalPaginas))}
                            disabled={pagina === totalPaginas}
                          >
                            Siguiente
                          </Button>
                        </MDBox>
                      </>
                    )}
                    <DocumentosAsociados
                      numeroCaso={numeroCaso}
                      onTicketClick={(ticketId) => {
                        setNumeroCaso(ticketId);
                        fetchData(); // carga de inmediato el nuevo ticket
                      }}
                    />

                    {adjuntos.length > 0 && (
                      <>
                        <MDTypography variant="h6" mt={5}>
                          Adjuntos
                        </MDTypography>
                        <DataTable
                          table={{ columns: columnsAdjuntos, rows: adjuntos }}
                          isSorted={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                        />
                      </>
                    )}
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ComentariosGlpi;
